import React, { forwardRef, useMemo } from "react";
import {
  Button,
  Divider,
  EmailIcon,
  FilesPdfIcon,
  Flex,
  Text,
  CloseIcon,
} from "@fluentui/react-northstar";
import moment from "moment";
import { Col, Progress, Row } from "antd";
import "./EvaluationReportDialog.scss";

const EvaluationReportDialogContent = forwardRef(
  (
    {
      gradingResult,
      rubrics,
      studentName,
      sendGradingReport,
      onClose,
      isSendingReport = false,
      translation,
      handleExportPDF = null,
    },
    ref
  ) => {
    const attachmentTypes = [
      {
        type: "text",
        label: translation.evaluationReport.attachmentTypes.textResponse,
      },
      {
        type: "voice",
        label: translation.evaluationReport.attachmentTypes.audioRecording,
      },
      {
        type: "video",
        label: translation.evaluationReport.attachmentTypes.videoSubmission,
      },
      {
        type: "file",
        label: translation.evaluationReport.attachmentTypes.fileAttachments,
      },
      {
        type: "link",
        label: translation.evaluationReport.attachmentTypes.researchLinks,
      },
    ];

    function getPerformanceLabelFromScore(score) {
      if (score === 0)
        return translation.evaluationReport.performanceLabels.notSubmitted;
      if (score >= 90)
        return `${translation.evaluationReport.performanceLabels.excellent} (${score}%)`;
      if (score >= 80)
        return `${translation.evaluationReport.performanceLabels.veryGood} (${score}%)`;
      if (score >= 70)
        return `${translation.evaluationReport.performanceLabels.good} (${score}%)`;
      if (score >= 60)
        return `${translation.evaluationReport.performanceLabels.fair} (${score}%)`;
      if (score >= 40)
        return `${translation.evaluationReport.performanceLabels.needsImprovement} (${score}%)`;
      return `${translation.evaluationReport.performanceLabels.poor} (${score}%)`;
    }

    const formatAnalysis = useMemo(() => {
      return attachmentTypes.map((item) => {
        const score = gradingResult?.formatAnalysis?.[item.type] || 0;
        return {
          ...item,
          score,
          performanceLable: getPerformanceLabelFromScore(score),
        };
      });
    }, [gradingResult, attachmentTypes]);

    return (
      <div className="evaluation-report-dialog-content">
        <div>
          <Text
            content={`${
              translation.evaluationReport.generatedOn
            } ${moment().format("DD/MM/YYYY")} • ${
              translation.evaluationReport.student
            }: ${studentName}`}
          />
          <Divider style={{ margin: "15px 0" }} />
        </div>
        <div ref={ref}>
          <Row gutter={[15, 15]} style={{ margin: 0 }}>
            <Col span={24}>
              <div className="execution-summary-wrapper">
                <Flex column gap="gap.small">
                  <Text
                    weight="semibold"
                    content={translation.evaluationReport.executiveSummary}
                  />
                  <div
                    dangerouslySetInnerHTML={{
                      __html: gradingResult?.summary || "",
                    }}
                  />
                </Flex>
              </div>
            </Col>
            <Col xs={24} md={12} lg={12} xl={12}>
              <Flex column gap="gap.small" className="rubric-wrapper">
                <Text
                  weight="semibold"
                  content={translation.evaluationReport.performanceBreakdown}
                />
                {rubrics.length > 0 &&
                  rubrics.map((rubric, i) => (
                    <div className="rubric-card" key={i + 1}>
                      <Flex column>
                        <Flex space="between">
                          <Text content={rubric.label} />
                          <Text
                            weight="semibold"
                            content={`${rubric.score}/${rubric.max}`}
                          />
                        </Flex>
                        <Progress
                          percent={Math.round(
                            (rubric.score / rubric.max) * 100
                          )}
                          showInfo={false}
                          strokeColor={rubric.color}
                        />
                        <Text size="small" content={rubric.comment} />
                      </Flex>
                    </div>
                  ))}
              </Flex>
            </Col>
            <Col xs={24} md={12} lg={12} xl={12}>
              <Flex column gap="gap.medium">
                <Text
                  weight="semibold"
                  content={translation.evaluationReport.aiInsights}
                />
                {/* Feedback */}
                <div className="feedback-wrapper">
                  <Flex column gap="gap.small">
                    <Text
                      weight="semibold"
                      content={translation.evaluationReport.feedback}
                    />
                    <div
                      dangerouslySetInnerHTML={{
                        __html: gradingResult?.overallFeedback || "",
                      }}
                    />
                  </Flex>
                </div>
                {/* Format Analysis */}
                <div className="format-analysis-wrapper">
                  <Flex column gap="gap.small">
                    <Text
                      weight="semibold"
                      content={translation.evaluationReport.formatAnalysis}
                    />
                    <Flex column gap="gap.smaller">
                      {formatAnalysis.map((item, i) => (
                        <Flex space="between" key={i + 1}>
                          <Text content={item.label} />
                          <Text
                            weight="semibold"
                            content={item.performanceLable}
                          />
                        </Flex>
                      ))}
                    </Flex>
                  </Flex>
                </div>
              </Flex>
            </Col>
          </Row>
        </div>
        <div>
          <Divider style={{ margin: "15px 0" }} />
          <Flex hAlign="center" gap="gap.smaller">
            <Button
              style={{ borderRadius: "5px" }}
              content={translation.evaluationReport.emailReport}
              icon={<EmailIcon />}
              onClick={() => sendGradingReport(ref)}
              loading={isSendingReport}
              disabled={isSendingReport}
            />
            <Button
              style={{ borderRadius: "5px" }}
              content={translation.evaluationReport.exportPdf}
              icon={<FilesPdfIcon />}
              onClick={handleExportPDF}
              primary
            />
            <Button
              style={{ borderRadius: "5px" }}
              content={translation.evaluationReport.closePreview}
              icon={<CloseIcon />}
              onClick={onClose}
            />
          </Flex>
        </div>
      </div>
    );
  }
);

export default EvaluationReportDialogContent;
